/**
 * Trigger-scoped dynamic field discovery for flow action nodes.
 *
 * Flows whose entry trigger is a `customer_event` can insert values pulled from
 * the trigger event (`event.*`) and the contact (`customer.*`). This hook reads
 * the entry trigger to derive `type` + `event`, samples the available fields via
 * `GET /flows/available-fields`, and labels them with the SAME curated taxonomy
 * the segment form/chips use (`@/lib/customer-data`) so marketers see "Membership
 * name", "Last visit", etc. — not raw dotted keys.
 *
 * No new API surface beyond the two flow endpoints; label resolution is pure
 * composition over the curated taxonomy + the existing event-property humanizer.
 */
import { startCase } from "lodash";
import { useMemo } from "react";
import useSWR from "swr";
import { flowService } from "@/api/flow";
import type { AutomationCondition } from "@/api/types/automation";
import type { FlowAvailableField, FlowFieldSource } from "@/api/types/flow";
import type { PropertyType } from "@/api/types/list";
import {
  customerEventPropertyOptions,
  labelEventProperty,
  parseCustomerPropertyLabel,
} from "@/lib/constants/lists";
import {
  NIGHTLIFE_CATEGORIES,
  NIGHTLIFE_TAXONOMY,
  SEGMENT_CATEGORIES,
  SEGMENT_TAXONOMY,
  type TaxonomyCategoryId,
  type TaxonomyField,
  WELLNESS_CATEGORIES,
  WELLNESS_TAXONOMY,
} from "@/lib/customer-data";
import { useFlowEditor } from "../flow-editor-context";

const OPTIONS_SWR_CONFIG = {
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
  keepPreviousData: true,
} as const;

const EVENT_GROUP_LABEL = "Event data";
const OTHER_CONTACT_GROUP_LABEL = "Other contact fields";

/** A single dynamic field offered by the picker. `value` is the runtime path. */
export interface FlowFieldOption {
  label: string;
  value: string;
  type: PropertyType;
  source: FlowFieldSource;
  category?: TaxonomyCategoryId;
}

export interface FlowFieldGroup {
  label: string;
  options: FlowFieldOption[];
}

/**
 * Reverse index of every curated *property* field keyed by its underlying
 * integration key (e.g. `properties.mariana_tek.membership.name`, `firstName`).
 * Built once; the segment form drives off the same taxonomy.
 */
const CURATED_FIELD_BY_KEY: Map<string, TaxonomyField> = (() => {
  const map = new Map<string, TaxonomyField>();
  const fields = [
    ...SEGMENT_TAXONOMY,
    ...NIGHTLIFE_TAXONOMY,
    // Guest since reads the base `createdAt` field, which is only the Zenoti
    // created date for Zenoti gyms, so it stays out of this shared index.
    ...WELLNESS_TAXONOMY.filter((field) => field.id !== "zn-guest-since"),
  ];
  for (const field of fields) {
    if (field.kind !== "property") continue;
    for (const source of Object.values(field.sources)) {
      if (source && "key" in source && source.key && !map.has(source.key)) {
        map.set(source.key, field);
      }
    }
  }
  return map;
})();

/** Category id -> { label, order } for grouping/ordering curated contact fields. */
const CATEGORY_META: Map<TaxonomyCategoryId, { label: string; order: number }> = (() => {
  const map = new Map<TaxonomyCategoryId, { label: string; order: number }>();
  for (const category of [...SEGMENT_CATEGORIES, ...NIGHTLIFE_CATEGORIES, ...WELLNESS_CATEGORIES]) {
    if (!map.has(category.id))
      map.set(category.id, { label: category.label, order: category.order });
  }
  return map;
})();

/**
 * Resolve a human label for a runtime field path WITHOUT the fetched field list
 * — used as a fallback by the shared chip when it can't find the path in the
 * hook's options. Mirrors curated taxonomy labels for `customer.*` and the
 * event-property humanizer for `event.*`.
 */
export function resolveFlowFieldMeta(path: string): { label: string } {
  if (path.startsWith("customer.")) {
    const key = path.slice("customer.".length);
    const curated = CURATED_FIELD_BY_KEY.get(key);
    return { label: curated?.label ?? parseCustomerPropertyLabel(key) };
  }
  if (path.startsWith("event.")) {
    return { label: labelEventProperty(path.slice("event.".length)) };
  }
  if (path.startsWith("gym.")) {
    return { label: startCase(path.replace(/\./g, " ")) };
  }
  return { label: path };
}

function getConditionValue(conditions: AutomationCondition[] | undefined, key: string) {
  const value = conditions?.find((condition) => condition.key === key)?.operators?.[0]?.value;
  return typeof value === "string" ? value : "";
}

/**
 * Read the entry trigger and, when it's a single resolvable `customer_event`,
 * surface its `type` + `event`. `enabled` gates every field fetch/picker.
 */
export function useFlowFieldTrigger(): { type: string; event: string; enabled: boolean } {
  const { triggers } = useFlowEditor();
  return useMemo(() => {
    const trigger = triggers?.[0];
    if (!trigger || trigger.type !== "customer_event") {
      return { type: "", event: "", enabled: false };
    }
    const type = getConditionValue(trigger.conditions, "event.type");
    const event = getConditionValue(trigger.conditions, "event.event");
    return { type, event, enabled: Boolean(type && event) };
  }, [triggers]);
}

interface FlowFieldOptionsResult {
  groups: FlowFieldGroup[];
  fieldByPath: Map<string, FlowFieldOption>;
  resolveLabel: (path: string) => { label: string; type?: PropertyType };
  isLoading: boolean;
  enabled: boolean;
}

/**
 * Fetch + label the dynamic fields for the flow's entry trigger. Returns curated
 * groups ("Event data" first, then contact fields by curated category) plus a
 * path -> option index and a `resolveLabel` helper the chip primitive reuses so
 * picker and chip always agree.
 */
export function useFlowFieldOptions(): FlowFieldOptionsResult {
  const { gym } = useFlowEditor();
  const gymId = gym?.id;
  const { type, event, enabled } = useFlowFieldTrigger();

  const swrKey = enabled && gymId ? `flow-fields|${gymId}|${type}|${event}` : null;
  const { data: fields, isLoading } = useSWR<Record<string, FlowAvailableField>>(
    swrKey,
    async () => {
      const { fields } = await flowService.listAvailableFields({
        gym: gymId!,
        type,
        event,
        sample: 1000,
      });
      return fields;
    },
    OPTIONS_SWR_CONFIG,
  );

  return useMemo(() => {
    const groups: FlowFieldGroup[] = [];
    const fieldByPath = new Map<string, FlowFieldOption>();
    const entries = Object.entries(fields ?? {});

    // ── Event data ────────────────────────────────────────────────────────
    // Reuse the segment event-property curation (label dictionaries +
    // internal/identifier filtering) by feeding the stripped keys through it.
    const eventProperties: Record<string, { type: PropertyType }> = {};
    for (const [path, field] of entries) {
      if (field.source !== "event") continue;
      eventProperties[path.slice("event.".length)] = { type: field.type };
    }
    if (Object.keys(eventProperties).length > 0) {
      const { options } = customerEventPropertyOptions(
        { properties: eventProperties },
        { provider: type },
      );
      const eventOptions: FlowFieldOption[] = options.map((option) => ({
        label: option.label,
        value: `event.${option.value}`,
        type: option.type,
        source: "event" as const,
      }));
      if (eventOptions.length > 0) {
        groups.push({ label: EVENT_GROUP_LABEL, options: eventOptions });
      }
    }

    // ── Contact fields (curated, grouped by category) ───────────────────────
    const byGroup = new Map<string, { order: number; options: FlowFieldOption[] }>();
    for (const [path, field] of entries) {
      if (field.source !== "customer") continue;
      const key = path.slice("customer.".length);
      const curated = CURATED_FIELD_BY_KEY.get(key);
      const groupLabel = curated
        ? (CATEGORY_META.get(curated.category)?.label ?? "Contact")
        : OTHER_CONTACT_GROUP_LABEL;
      const order = curated
        ? (CATEGORY_META.get(curated.category)?.order ?? Number.MAX_SAFE_INTEGER - 1)
        : Number.MAX_SAFE_INTEGER;
      const option: FlowFieldOption = {
        label: curated?.label ?? parseCustomerPropertyLabel(key),
        value: path,
        type: field.type,
        source: "customer",
        category: curated?.category,
      };
      const bucket = byGroup.get(groupLabel) ?? { order, options: [] };
      bucket.options.push(option);
      byGroup.set(groupLabel, bucket);
    }
    const contactGroups = [...byGroup.entries()]
      .sort((a, b) => a[1].order - b[1].order || a[0].localeCompare(b[0]))
      .map(([label, bucket]) => ({
        label,
        options: bucket.options.sort((a, b) => a.label.localeCompare(b.label)),
      }));
    groups.push(...contactGroups);

    for (const group of groups) {
      for (const option of group.options) fieldByPath.set(option.value, option);
    }

    const resolveLabel = (path: string) => {
      const option = fieldByPath.get(path);
      if (option) return { label: option.label, type: option.type };
      return resolveFlowFieldMeta(path);
    };

    return { groups, fieldByPath, resolveLabel, isLoading, enabled };
  }, [fields, type, isLoading, enabled]);
}

/**
 * Sample example values for a single field path (used by the condition editor's
 * value dropdown). Scoped to the entry trigger's `type` + `event`.
 */
export function useFlowFieldValues(field?: string): {
  options: Array<{ label: string; value: string }>;
  isLoading: boolean;
} {
  const { gym } = useFlowEditor();
  const gymId = gym?.id;
  const { type, event, enabled } = useFlowFieldTrigger();

  const swrKey =
    enabled && gymId && field ? `flow-field-values|${gymId}|${type}|${event}|${field}` : null;
  const { data, isLoading } = useSWR<Array<{ label: string; value: string }>>(
    swrKey,
    async () => {
      const { values } = await flowService.listAvailableFieldValues({
        gym: gymId!,
        type,
        event,
        field: field!,
        sample: 1000,
      });
      return (values ?? []).map((value) => ({ label: String(value), value: String(value) }));
    },
    OPTIONS_SWR_CONFIG,
  );

  return { options: data ?? [], isLoading };
}
